<template>
  <div class="articles-container">
    <h2 class="h3">
      Standard Deviation Calculator
    </h2>
    <div id="heading1" class="article-section">
      <h3 class="introduction h4">
        What Is Standard Deviation?
      </h3>
      <p class="bottom-margins">
        Standard deviation measures the amount of variation or dispersion in a dataset. Specifically, it shows us what is the average distance of each observation from the mean. Standard deviation is often abbreviated as <span class="bold">SD</span>. In mathematical terms, we denote the population standard deviation with the lowercase Greek letter sigma <span class="bold">σ</span> and the lowercase <span class="bold">s</span> for the sample standard deviation.
      </p>
      <p> You can find both with our Standard Deviation Calculator. But to interpret the results, you must fully grasp the definition.</p>
    </div>

    <div id="heading2" class="article-section">
      <h3 class="introduction h4">
        What Does Standard Deviation Mean?
      </h3>
      <p class="bottom-margins">
        To reiterate, standard deviation measures how dispersed a set of values is in regard to the mean. These values can either be below or above the mean, depending on the standard deviation curve. In that sense, a dataset with a low standard deviation indicates that data is closer to the mean, whereas a high standard deviation indicates that observations are more spread out.
      </p>
      <p class="bottom-margins">
        But what if there is no dispersion? In some cases, the standard deviation in a dataset is 0, meaning all of the values are identical. Think of a dataset of aggregated information on people’s height. If every subject has exactly the same height as all the others, then there’s no variability in the dataset.
      </p>
      <p class="paragraph-spacing-bottom">
        The following image depicts two graphs with a standard deviation different than 0:
      </p>
      <img loading="lazy" class="img-top-margin-only img-first" src="../../../../assets/images/calculators/standard-deviation/sd2-01-desktop.png" alt="Sample Standard Deviation graph.">
      <img loading="lazy" class="img-top-margin-only margin-bottom-40 img-second" src="../../../../assets/images/calculators/standard-deviation/sd-02-desktop.png" alt="Population Standard Deviation graph.">
      <p class="margin-bottom-40 paragraph-spacing-top">
        In the upper graph, the data points are more dispersed, therefore the standard deviation is higher. Meanwhile, the graph below shows a distribution with observations that are more clustered around the mean, resulting in a lower standard deviation.
      </p>
      <p class="bold-text bottom-margins">
        The 68–95–99.7 Rule
      </p>
      <p class="bottom-margins">
        Standard deviation is a particularly useful measure for the <nuxt-link to="/tutorials/statistics-tutorials/normal-distribution/" target="_blank">
          normal distribution
        </nuxt-link> (also known as Gaussian distribution).
      </p>
      <p> Because the shape of the normal distribution is symmetrical and bell-like, the probability of observing a value to the left or right of the mean is equal. This means that the distribution is balanced and most of its values are in the center, with no long tail on either side of the mean. Put simply, both sides are mirror images.</p>
      <img loading="lazy" class="img-top-margin-only margin-bottom-40 img-third" src="../../../../assets/images/calculators/standard-deviation/sd-03-desktop.png" alt="Normal distribution graph.">
      <p class="margin-bottom-24 paragraph-spacing-top">
        Many variables in life follow the normal distribution, including people’s weights and heights, standardized test scores, errors in measurement, or manufacturing defects.
      </p>
      <p class="margin-bottom-paragraph">
        You can use the Standard Deviation Calculator and <nuxt-link to="/calculators/mean-median-mode-calculator/" target="_blank">
          Mean Calculator
        </nuxt-link> to quickly find out how clustered the data values are around the mean. According to the <a href="https://en.wikipedia.org/wiki/68%E2%80%9395%E2%80%9399.7_rule">68-95-99.7 rule</a>, also known as the <span class="bold">empirical rule</span>, the percentage of values will fall within three standard deviations, more specifically:
      </p>
      <ul class="ul ul-spacing ">
        <li>Approximately 68% of all data points fall in the interval μ ± σ (within <span class="bold">1 SD</span> of the mean).</li>
        <li> Approximately 95% of all data points fall in the interval μ ± 2σ (within <span class="bold">2 SD</span> of the mean). </li>
        <li>Approximately 99.7% of all data points fall in the interval μ ± 3σ (within <span class="bold">3 SD</span> of the mean).</li>
      </ul>
      <img loading="lazy" class="img-top-margin-only img-fourth" src="../../../../assets/images/calculators/standard-deviation/sd-04-desktop.png" alt="Standard Deviation of the mean graph.">
      <img loading="lazy" class="img-top-margin-only margin-bottom-40 img-fifth" src="../../../../assets/images/calculators/standard-deviation/sd-05-desktop.png" alt="The three Standard Deviation of the mean graph.">
      <p class="paragraph-spacing-top">
        Suppose that you have a dataset with student exam scores; the values follow a normal distribution with a mean of 80 and a standard deviation of 10. According to the 68-95-99.7 rule, the scores that fall within different intervals of the distribution are as follows:
        <ul class="ul">
          <li>Approximately 68% of the scores will fall between 70 and 90 SD.</li>
          <li>Approximately 95% of the scores will fall between 60 and 100 SD.</li>
          <li>Approximately 99.7% of the scores will fall between 50 and 110 SD.</li>
        </ul>
      </p>
      <img loading="lazy" class="img-top-margin-only no-bottom-margin img-sixth" src="../../../../assets/images/calculators/standard-deviation/sd2-06-desktop.png" alt="Standard Deviations in a Normal Distribution.">
    </div>

    <div id="heading3" class="article-section">
      <h3 class="introduction h4">
        Why Is Standard Deviation Important?
      </h3>
      <p class="margin-bottom-24">
        Standard deviation is an essential statistic for many types of analysis, used by statisticians, data analysts, and data scientists across the board. In this article, we’ll look at just some of the widespread applications of this measure.
      </p>
      <p class="bottom-margins bold">
        Measuring Dispersion
      </p>
      <p>
        Standard deviation tells you how widely values are spread out from the mean. For example, consider the following two populations:
      </p>
      <ul class="ul bottom-margins">
        <li>{2,2,16,16}</li>
        <li> {8,8,10,10}</li>
      </ul>
      <p class="bottom-margins">
        Both of them have a mean of 9 and their standard deviations are 7 and 1, respectively. From that, we can tell that the second population has a much smaller standard deviation because its values are closer to the mean.
      </p>
      <p class="bottom-margins bold">
        Evaluating Data Quality
      </p>
      <p class="bottom-margins">
        Standard deviation is useful when identifying data points at an abnormal distance away from the mean; otherwise known as outliers whose existence can detriment any statistical findings.
      </p>
      <p class="bottom-margins">
        Some practitioners argue that observations that lie three standard deviations away from the mean are considered outliers.
      </p>
      <p class="bottom-margins bold">
        Making Statistical Inferences
      </p>
      <p class="bottom-margins">
        We use standard deviation in calculate <nuxt-link to="/tutorials/statistics-tutorials/point-estimates-confidence-intervals/" target="_blank">
          confidence intervals
        </nuxt-link> and <nuxt-link to="/tutorials/statistics-tutorials/null-hypothesis/" target="_blank">
          hypothesis testing
        </nuxt-link>. It provides information on the data’s variability and helps to make statistical inferences about the population.
      </p>
      <p class="bottom-margins bold">
        Modeling
      </p>
      <p>Standard deviation plays a crucial role in many statistical models, such as linear regression and normal distributions. It is often used to quantify the variation of errors or residuals in a model and when evaluating model performance.</p>
    </div>

    <div id="heading4" class="article-section">
      <h3 class="introduction h4">
        How to Calculate the Standard Deviation
      </h3>
      <p class="bottom-margins">
        Our Standard Deviation Calculator is a useful resource when you want to streamline your work. But knowing how to calculate it yourself will help you better interpret the results.
      </p>
      <p class="bottom-margins">
        The standard deviation calculation varies depending on whether your data is from a <span class="bold">population</span> or a <span class="bold">sample</span>.
      </p><p class="bottom-margins">
        <span class="bold">Population standard deviation</span>, denoted by σ, measures the variability of the entire population. To obtain it we sum, the squared differences of each data point from the population mean and divide by the total number of data points. The population standard deviation is the square root of the result.
      </p>
      <p class="spacing-bottom">
        Use the following formula to calculate the <span class="bold">population standard deviation:</span>
      </p>
      <p class="formulas-sd">
        \[\sigma=\sqrt{\sum_{i=1}^{n}\frac{(X_i-\mu)^2}{n}}\]
      </p>
      <p class="bottom-margins">
        Here μ, represents the population mean and n is the number of observations in the dataset.
      </p>
      <p class="spacing-bottom">
        Meanwhile, we calculate the <span class="bold">sample standard deviation</span>, denoted by , as follows:
      </p>
      <p class="formulas-sd">
        \[s=\sqrt{\sum_{i=1}^{n}\frac{(X_i-\bar{X})^2}{n-1}}\]
      </p>
      <p class="bottom-margins spacing-top">
        Here <span class="inline-formulas">\[\bar{X}\]</span> is the sample mean and n is the number of observations in the sample.
      </p>
      <p class="bottom-margins">
        Please note that when calculating sample variance, we divide the sum of the squared deviations of each observation from the population mean by <span class="bold"> n-1</span>, instead of <span class="bold">n</span>. Known as <a href="https://en.wikipedia.org/wiki/Bessel%27s_correction" target="_blank">Bessel's correction,</a> this measure allows us to correct the bias in the population standard deviation estimatе due to the finite sample size.
      </p>
      <p class="bottom-margins">
        Put simply, by using n-1 instead of n in the denominator, we can produce unbiased estimates of our population. In practice, it’s more common to calculate the sample standard deviation than the population standard deviation since sample data is easier to collect. (Still, our Standard Deviation Calculator allows you to find both.)
      </p>
      <p>You can also note that in both equations we square the differences between each data point and the mean. Squaring the differences has two main purposes:</p>
      <ul class="ul bottom-margins">
        <li>Non-negative dispersion</li>
        <li>Larger observation sets</li>
      </ul>
      <p class="bottom-margins">
        First, by squaring the numbers, we always get non-negative computations. In essence, dispersion is about distance, which cannot be negative. If we don’t elevate to the second degree, we obtain both positive and negative values. In turn, both types of values cancel out each other when summed, leaving us with no information about the dispersion.
      </p>
      <p>Second, squaring amplifies the effect of large differences. This means that outliers (observations that are far from the mean) have a greater impact on the variance than those closer to the mean. For example, if the mean is 0 and you have an observation of 100, then the squared spread is 10,000. But without squaring, this observation would contribute a spread of only 100, which might not fully capture the magnitude of its deviation from the mean.</p>
    </div>

    <div id="heading5" class="article-section">
      <h3 class="introduction h4">
        Standard Deviation vs Variance
      </h3>
      <p class="bottom-margins">
        Standard deviation and variance are two of the most used statistical measures of variability. Variance is equal to the average of the squared deviations around the mean, whereas the standard deviation is equal to the square root of variance.
      </p>
      <p>When it comes to units of measurement, the two are stated as follow:</p>
      <ul class="ul margin-bottom-40">
        <li>Variance is expressed in squared units (e.g. ounces squared).</li>
        <li>Standard deviation is expressed in the same units of measurement as the original units (e.g. ounces).</li>
      </ul>
      <p class="bottom-margins">
        Unlike variance, we measure standard deviation in the same units as the observations. This makes it the preferred method to assess variability, especially when it comes to practical applications such as data analysis, decision-making, and reporting.
      </p>
      <p>
        Still, you can find both using our <nuxt-link to="/calculators/variance-calculator/" target="_blank">
          Variance Calculator
        </nuxt-link> and Standard Deviation Calculator.
      </p>
    </div>
  </div>
</template>

<script>
export default {
  mounted() {
    this.renderMathJax();
  },
  methods: {
    renderMathJax() {
      if (window.MathJax) {
        window.MathJax.Hub.Config({
          tex2jax: {
            inlineMath: [['$', '$'], ['\\(', '\\)']],
            processEscapes: true,
          },
          'HTML-CSS': {
            linebreaks: { automatic: true, width: 'container' },
          },
          SVG: {
            linebreaks: { width: '10000px' },
          },
          jax: ['input/TeX', 'output/SVG'],
        });
        window.MathJax.Hub.Queue(['Typeset', window.MathJax.Hub]);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.articles-container {
  .article-section {
    p {
      margin: 0;
    }

    .colored-texts {
      font-weight: 600;
      color: var(--accent-shade-3);
    }

    .bold {
      font-weight: 600;
    }

    .bold-text {
      font-size: 20px;
      color: var(--main-dark);
      font-weight: 600;
    }

    .paragraph-spacing-bottom {
      margin-bottom: 15px;
    }

    .spacing-top {
      margin-top: var(--spacing-16);
    }

    .spacing-bottom {
      margin-bottom: var(--spacing-16);
    }

    .margin-bottom-paragraph {
      margin-bottom: var(--spacing-24);
    }

    .paragraph-spacing-top {
      margin-top: var(--spacing-40);
    }

    .formulas-sd {
      margin: var(--spacing-24) 0;
    }

    .img-top-margin-only {
      margin: var(--spacing-40) 0 0;
    }

    .margin-bottom-40 {
      margin-bottom: var(--spacing-40);
    }

    .margin-bottom-24 {
      margin-bottom: var(--spacing-24);
    }

    .calculator-link {
      text-decoration: none;
    }

    .ul {
      margin-top: var(--spacing-16);
    }

    .introduction.h4 {
      margin-top: 0;
    }
  }
}

@include bp($bp-mobile) {
  .row {
    .articles-container {
      .article-section {
        .paragraph-spacing-bottom {
          margin-bottom: 0;
        }

        .paragraph-spacing-top {
          margin-top: 0;
        }

        .margin-bottom-paragraph {
          margin-bottom: var(--spacing-16);
        }

        .margin-bottom-40 {
          margin-bottom: var(--spacing-32) !important;
        }

        .margin-bottom-24 {
          margin-bottom: var(--spacing-16);
        }

        .no-bottom-margin {
          margin-bottom: 0 !important;
        }

        .formulas-sd {
          margin: var(--spacing-16) 0;
        }

        .img-top-margin-only {
          margin: var(--spacing-32) 0 0;
        }

        .img-second,
        .img-third,
        .img-sixth,
        .img-eight {
          margin: var(--spacing-32) 0;
        }
      }
    }
  }
}

@import '../../../../assets/sass/ds/calculators/articles.scss';
</style>
